import React, { useState } from 'react';
import { useToast } from '../components/ToastContext';
import { useI18n } from '../i18n';
import './Steps.css';

const API_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * Step 1: Multi-File Upload & Conversion
 * Upload PDF files and convert them to Markdown using Datalab API
 * Tracks timing for Datalab API call
 */

function Step1Upload({ onNext }) {
  const { showToast } = useToast();
  const { t } = useI18n();
  const [files, setFiles] = useState([]);
  const [error, setError] = useState('');
  const maxFiles = 1; // Default max files allowed

  // Handle file selection
  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    validateAndAddFiles(selectedFiles);
  };

  // Handle drag and drop
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const droppedFiles = Array.from(e.dataTransfer.files);
    validateAndAddFiles(droppedFiles);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.classList.add('drag-over');
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.classList.remove('drag-over');
  };

  const validateAndAddFiles = (selectedFiles) => {
    // Validate file types
    const invalidFiles = selectedFiles.filter(file => file.type !== 'application/pdf');
    if (invalidFiles.length > 0) {
      const msg = `${t('step1.validation.invalidType')}: ${invalidFiles.map(f => f.name).join(', ')}`;
      setError(msg);
      showToast(msg, 'error');
      return;
    }

    // Validate file sizes (50MB each)
    const largeFiles = selectedFiles.filter(file => file.size > 50 * 1024 * 1024);
    if (largeFiles.length > 0) {
      const msg = `${t('step1.validation.fileTooLarge')}: ${largeFiles.map(f => f.name).join(', ')}`;
      setError(msg);
      showToast(msg, 'error');
      return;
    }

    // Check max files limit
    const totalFiles = files.length + selectedFiles.length;
    if (totalFiles > maxFiles) {
      const msg = t('step1.validation.maxFiles', { max: maxFiles, current: files.length, adding: selectedFiles.length });
      setError(msg);
      showToast(msg, 'error');
      return;
    }

    setFiles(prev => [...prev, ...selectedFiles]);
    setError('');
  };

  // Remove a file from the list
  const handleRemoveFile = (index) => {
    setFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
  };

  // Convert files and proceed - Start API call and immediately navigate to Step 2
  const handleConvertAndProceed = async () => {
    if (files.length === 0) {
      const msg = t('step1.validation.noFiles');
      setError(msg);
      showToast(msg, 'error');
      return;
    }

    setError('');
    
    // Create a promise for the Datalab API call
    const datalabPromise = (async () => {
      const markdownFiles = [];
      let datalabStartTime = null;
      let datalabEndTime = null;

      try {
        for (let i = 0; i < files.length; i++) {
          const file = files[i];

          try {
            const formData = new FormData();
            formData.append('file', file);

            // Record start time before API call
            datalabStartTime = datalabStartTime || Date.now();

            const response = await fetch(`${API_URL}/convert`, {
              method: 'POST',
              body: formData
            });

            // Record end time after API response
            datalabEndTime = Date.now();

            const data = await response.json();

            if (!response.ok) {
              throw new Error(data.error || `Server error: ${response.status}`);
            }

            if (data.markdown) {
              markdownFiles.push({
                fileName: file.name,
                markdownContent: data.markdown,
                images: data.images || {},           // Object format: { fileName: cdnUrl }
                imageList: data.imageList || [],     // Array format: [{ id, fileName, url }]
                segments: data.segments || [],       // Array format: [{ title, fromPage, toPage, thumbs }]
                thumbnails: data.thumbnails || [],   // Array format: [{ page, imageData }]
                requestId: data.requestId || null    // Datalab request ID
              });
              console.log(`✓ تم تحويل ${file.name} مع ${(data.segments || []).length} مقطع و ${(data.thumbnails || []).length} صورة`);
            } else {
              throw new Error('لم يتم استلام محتوى Markdown من الخادم');
            }

          } catch (fileError) {
            console.error(`✗ فشل تحويل ${file.name}:`, fileError.message);
            throw new Error(`تحذير: فشل تحويل ${file.name} - ${fileError.message}`);
          }
        }

        if (markdownFiles.length === 0) {
          throw new Error(t('step1.toast.convertFailed'));
        }

        // Calculate Datalab API time
        const datalabTime = datalabEndTime && datalabStartTime ? datalabEndTime - datalabStartTime : 0;

        return { markdownFiles, datalabTime, error: null };
      } catch (err) {
        console.error('Conversion error:', err);
        return { 
          markdownFiles: [], 
          datalabTime: 0, 
          error: err.message || t('step1.toast.convertFailed') 
        };
      }
    })();

    // Immediately navigate to Step 2 with the promise
    onNext({ datalabPromise });
  };


  return (
    <div className="step-container">
      <div className="step-header">
        <h2>{t('step1.title')}</h2>
        <p className="step-description">
          {t('step1.description')}
        </p>
      </div>

      <div className="step-content">
        {/* File Upload Area */}
        <div
          className={`upload-area ${files.length > 0 ? 'has-files' : ''}`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          <div className="upload-icon">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96zM14 13v4h-4v-4H7l5-5 5 5h-3z" />
            </svg>
          </div>

          <h3>{t('step1.dropzone')}</h3>
          <p>{t('step1.dropzoneHint')}</p>

          <label htmlFor="fileInput" className="btn-browse">
            {t('step1.browseFiles')}
          </label>
          <input
            type="file"
            id="fileInput"
            multiple
            accept=".pdf,application/pdf"
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
        </div>

        {/* Error Message */}
        {error && <div className="alert alert-error">{error}</div>}

        {/* Selected Files List */}
        {files.length > 0 && (
          <div className="files-list">
            <h3>{t('step1.uploadedFiles')} ({files.length})</h3>
            <div className="files-grid">
              {files.map((file, index) => (
                <div key={index} className="file-card">
                  <div className="file-icon">
                    📄
                  </div>
                  <div className="file-info">
                    <div className="file-name">{file.name}</div>
                    <div className="file-meta">
                      <span className="file-size">{(file.size / (1024 * 1024)).toFixed(1)} {t('step1.fileSize')}</span>
                      <span className="file-status">
                        • {t('step1.fileUploaded')}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => handleRemoveFile(index)}
                    className="file-remove"
                  >
                    🗑️
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="step-actions step-actions-right">
        <button
          onClick={handleConvertAndProceed}
          className="btn-primary"
          disabled={files.length === 0}
        >
          {t('step1.convertButton')}
        </button>
      </div>
    </div>
  );
}

export default Step1Upload;
